import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Card, Row, Col, Input, Typography, Space, Avatar, Tag, Skeleton } from 'antd';
import { apiService } from '../../services/api';
import { homeQueryKeys } from '../../lib/queryClient';
import { useNavigateWithScroll } from '../../hooks/useNavigateWithScroll';
import AdvertisementDisplay from '../../components/Common/AdvertisementDisplay';
import OptimizedImage from '../../components/Common/OptimizedImage';
import { AdvertisementType } from '../../types';
import '../../styles/colors.css';

const { Title, Paragraph, Text } = Typography;
const { Search } = Input;

interface PopularCategory {
  id: number;
  name: string;
  description?: string;
  categoryImage?: string;
  mentorCount: number;
}

const Home: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigateWithScroll();

  const {
    data: popularCategories = [],
    isLoading: categoriesLoading,
    isError: categoriesError,
    refetch: refetchCategories,
  } = useQuery({
    queryKey: homeQueryKeys.popularCategories,
    queryFn: async () => {
      const response = await apiService.getPopularCategories(8);
      if (!response.success) throw new Error(response.message);
      return response.data ?? [];
    },
    staleTime: 1000 * 60 * 5,
    retry: 1,
    retryDelay: 2000,
  });

  const {
    data: popularMentors = [],
    isLoading: mentorsLoading,
    isError: mentorsError,
    refetch: refetchMentors,
  } = useQuery({
    queryKey: homeQueryKeys.popularMentors,
    queryFn: async () => {
      const response = await apiService.getPopularMentors();
      if (!response.success) throw new Error(response.message);
      return response.data ?? [];
    },
    staleTime: 1000 * 60 * 5,
    retry: 1,
    retryDelay: 2000,
  });

  const handleSearch = (value: string) => {
    if (value.trim()) {
      // Arama terimini URL parametresi olarak mentor search sayfasına gönder
      navigate(`/mentor-search?search=${encodeURIComponent(value.trim())}`);
    }
  };

  return (
    <>
      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-tertiary) 100%)',
        minHeight: '100vh',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        padding: '40px 0',
        color: 'var(--text-primary)'
      }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
          <Row align="middle" gutter={[24, 24]}>
            <Col xs={24} lg={14}>
              <div>
                <Title level={1} style={{ 
                  fontSize: 'clamp(2rem, 5vw, 3rem)',
                  fontWeight: '700',
                  marginBottom: '20px',
                  lineHeight: '1.2',
                  color: 'var(--text-primary)'
                }}>
                  Kariyerine<br />
                  <span style={{ 
                    color: 'var(--primary-600)'
                  }}>
                    yön ver, sektörde<br />
                    fark yarat.
                  </span>
                </Title>
                
                <Paragraph style={{ 
                  fontSize: 'clamp(0.95rem, 2vw, 1.1rem)',
                  marginBottom: '24px',
                  color: 'var(--text-secondary)',
                  lineHeight: '1.6'
                }}>
                  Herkes İçin Ulaşılabilir Mentörlük
                </Paragraph>

                {/* Search Box */}
                <div style={{ marginBottom: '24px', maxWidth: '100%' }}>
                  <Search
                    placeholder="Mentor, kurs veya beceri ara..."
                    enterButton={<Button className="btn-primary" aria-label="Arama yap">Ara</Button>}
                    size="large"
                    style={{
                      borderRadius: '50px'
                    }}
                    onSearch={handleSearch}
                    aria-label="Mentor, kurs veya beceri arama kutusu"
                  />
                </div>

                {/* CTA Buttons */}
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                {!isAuthenticated ? (
                    <>
                    <Button 
                      size="large"
                      className="btn-primary"
                      style={{
                        height: '48px',
                        padding: '0 24px',
                        flex: '1 1 auto',
                        minWidth: '140px'
                      }}
                    >
                        <Link to="/register-mentee" style={{ color: 'inherit', textDecoration: 'none' }}>
                        Hemen Başla
                      </Link>
                    </Button>
                    <Button 
                      size="large"
                        className="btn-secondary"
                      style={{
                        height: '48px',
                          padding: '0 24px',
                          flex: '1 1 auto',
                          minWidth: '140px'
                        }}
                      >
                        <Link to="/login" style={{ color: 'inherit', textDecoration: 'none' }}>
                        Giriş Yap
                      </Link>
                    </Button>
                    </>
                ) : (
                    <Button 
                      size="large"
                      className="btn-primary"
                      style={{
                        height: '48px',
                        padding: '0 24px',
                        width: '100%'
                      }}
                    >
                      <Link 
                        to={
                          user?.role === 'Admin' ? '/admin/panel' :
                          user?.role === 'Mentor' ? '/profile' :
                          user?.role === 'Mentee' ? '/mentee-profile' :
                          user?.role === 'Company' ? '/company-panel' :
                          '/dashboard'
                        } 
                        style={{ color: 'inherit', textDecoration: 'none' }}
                      >
                        Dashboard'a Git
                      </Link>
                    </Button>
                )}
                </div>
              </div>
            </Col>
            
            <Col xs={24} lg={10}>
              <div style={{ textAlign: 'center' }}>
                <img
                  src="/images/backgrounds/hero.png"
                  alt="Hero"
                  loading="lazy"
                  style={{ 
                    maxWidth: '100%', 
                    height: 'auto',
                    borderRadius: '12px'
                  }}
                />
              </div>
            </Col>
          </Row>
        </div>
      </section>
      {/* /Hero Section */}

      {/* Banner Reklam */}
      <section style={{ padding: '32px 0', background: 'var(--bg-primary)' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
          <AdvertisementDisplay 
            pagePath="/" 
            type={AdvertisementType.Banner}
            position="top"
          />
        </div>
      </section>

      {/* En Popüler Kategoriler Section */}
      <section style={{ 
        padding: '60px 0', 
        background: 'var(--bg-primary)' 
      }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <Text className="text-accent" style={{ 
              fontSize: 'clamp(0.95rem, 2vw, 1.1rem)',
              fontWeight: '600',
              marginBottom: '8px',
              display: 'block'
            }}>
              Kategoriler
            </Text>
            <Title level={2} style={{ 
              fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
              fontWeight: '700',
              marginBottom: '16px',
              color: 'var(--text-primary)'
            }}>
              En popüler Kategoriler
            </Title>
            <Paragraph style={{ 
              fontSize: 'clamp(0.95rem, 2vw, 1.1rem)',
              color: 'var(--text-secondary)',
              maxWidth: '600px',
              margin: '0 auto',
              padding: '0 16px'
            }}>
              Aradığınız kategorilerde alanında uzman eğitmenlerden destek alın. Yeteneklerinizi geliştirin.
            </Paragraph>
          </div>

          <Row gutter={[16, 16]}>
            {categoriesError ? (
              <Col span={24}>
                <Card className="app-card" style={{ textAlign: 'center', padding: '32px 24px' }}>
                  <Text style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '16px' }}>
                    Kategoriler şu an yüklenemedi. Bağlantı veya sunucu kaynaklı bir sorun olabilir.
                  </Text>
                  <Button type="primary" onClick={() => refetchCategories()}>
                    Tekrar Dene
                  </Button>
                </Card>
              </Col>
            ) : categoriesLoading ? (
              Array.from({ length: 8 }).map((_, index) => (
                <Col xs={12} sm={12} md={6} lg={6} key={`category-skeleton-${index}`}>
                  <Card className="app-card" style={{ textAlign: 'center', height: '100%' }} bodyStyle={{ padding: '20px 16px' }}>
                    <div style={{ 
                      width: 'clamp(80px, 15vw, 120px)',
                      height: 'clamp(80px, 15vw, 120px)',
                      margin: '0 auto 16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Skeleton.Image 
                        active 
                        style={{ 
                          width: '100%', 
                          height: '100%',
                          borderRadius: '8px'
                        }} 
                      />
                    </div>
                    <div style={{ width: '100%', marginBottom: '8px' }}>
                      <Skeleton active paragraph={false} title={{ width: '70%', style: { margin: '0 auto' } }} />
                    </div>
                    <div style={{ width: '100%' }}>
                      <Skeleton active paragraph={false} title={false} style={{ width: '60%', margin: '0 auto', height: '16px' }} />
                    </div>
                  </Card>
                </Col>
              ))
            ) : (
              popularCategories.map((category) => (
                <Col xs={12} sm={12} md={6} lg={6} key={category.id}>
                  <Card 
                    hoverable
                    className="app-card"
                    style={{ textAlign: 'center', height: '100%', cursor: 'pointer' }}
                    bodyStyle={{ padding: '20px 16px' }}
                    onClick={() => {
                      navigate(`/mentor-search?category=${encodeURIComponent(category.name)}`);
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={`${category.name} kategorisindeki mentorları görüntüle`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/mentor-search?category=${encodeURIComponent(category.name)}`);
                      }
                    }}
                  >
                    <div style={{ 
                      width: 'clamp(80px, 15vw, 120px)',
                      height: 'clamp(80px, 15vw, 120px)',
                      margin: '0 auto 16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {category.categoryImage ? (
                        <OptimizedImage
                          src={`data:image/jpeg;base64,${category.categoryImage}`}
                          alt={`${category.name} kategorisi görseli`}
                          width="100%"
                          height="100%"
                          objectFit="cover"
                          loading="lazy"
                          placeholder="blur"
                          style={{ borderRadius: '8px' }}
                          aria-label={`${category.name} kategorisi görseli`}
                        />
                      ) : (
                        <div 
                          style={{
                            width: '100%',
                            height: '100%',
                            backgroundColor: '#f0f0f0',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '2rem',
                            color: '#999'
                          }}
                          role="img"
                          aria-label={`${category.name} kategorisi placeholder`}
                        >
                          {category.name.charAt(0)}
                        </div>
                      )}
                    </div>
                    <Title level={4} style={{ 
                      fontSize: 'clamp(0.95rem, 2vw, 1.2rem)',
                      marginBottom: '8px',
                      color: 'var(--text-primary)'
                    }}>
                      {category.name}
                    </Title>
                    <Text style={{ 
                      fontSize: 'clamp(0.8rem, 1.5vw, 0.9rem)',
                      color: 'var(--text-secondary)',
                      display: 'block'
                    }}>
                      {category.mentorCount} mentor
                    </Text>
                  </Card>
                </Col>
              ))
            )}
          </Row>

          <div style={{ textAlign: 'center', marginTop: '32px' }}>
            <Button 
              size="large"
              className="btn-primary"
              style={{
                height: '48px',
                padding: '0 24px'
              }}
              onClick={() => {
                navigate('/categories');
              }}
            >
              Tüm Kategorileri Görüntüle
            </Button>
          </div>
        </div>
      </section>
      {/* /Kategorilerimiz Section */}

      <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px 40px' }}>
          <AdvertisementDisplay
            pagePath="/"
            type={AdvertisementType.Card}
            position="inline"
          />
      </div>

      {/* Popüler Mentörler Section */}
      <section style={{ 
        padding: '80px 0', 
        background: 'linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-tertiary) 100%)' 
      }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <Text className="text-accent" style={{ 
              fontSize: '1.1rem',
              fontWeight: '600',
              marginBottom: '8px',
              display: 'block'
            }}>
              Mentörler
            </Text>
            <Title level={2} style={{ 
              fontSize: '2.5rem',
              fontWeight: '700',
              marginBottom: '16px',
              color: '#2c3e50'
            }}>
              Popüler Mentörler
            </Title>
            <Paragraph style={{ 
              fontSize: '1.1rem',
              color: 'var(--text-secondary)',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              Alanında uzman mentörlerimizle tanışın ve kariyerinizde ilerleyin
            </Paragraph>
          </div>

          <Row gutter={[16, 16]}>
            {mentorsError ? (
              <Col span={24}>
                <Card className="app-card" style={{ textAlign: 'center', padding: '32px 24px' }}>
                  <Text style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '16px' }}>
                    Popüler mentörler şu an yüklenemedi. Bağlantı veya sunucu kaynaklı bir sorun olabilir.
                  </Text>
                  <Button type="primary" onClick={() => refetchMentors()}>
                    Tekrar Dene
                  </Button>
                </Card>
              </Col>
            ) : mentorsLoading ? (
              Array.from({ length: 8 }).map((_, index) => (
                <Col xs={12} sm={12} md={6} lg={6} key={`mentor-skeleton-${index}`}>
                  <Card className="app-card" style={{ textAlign: 'center', height: '100%' }} bodyStyle={{ padding: '20px 16px' }}>
                    <div style={{ 
                      margin: '0 auto 12px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: 'clamp(60px, 12vw, 80px)',
                      height: 'clamp(60px, 12vw, 80px)'
                    }}>
                      <Skeleton.Avatar 
                        active 
                        size={80} 
                        shape="circle"
                        style={{ 
                          width: 'clamp(60px, 12vw, 80px)', 
                          height: 'clamp(60px, 12vw, 80px)' 
                        }} 
                      />
                    </div>
                    <div style={{ width: '100%', marginBottom: '8px' }}>
                      <Skeleton active paragraph={false} title={{ width: '80%', style: { margin: '0 auto' } }} />
                    </div>
                    <div style={{ width: '100%', marginBottom: '8px' }}>
                      <Skeleton active paragraph={false} title={false} style={{ width: '70%', margin: '0 auto', height: '14px' }} />
                    </div>
                    <div style={{ width: '100%', marginBottom: '12px', display: 'flex', justifyContent: 'center', gap: '4px' }}>
                      <Skeleton.Button active size="small" style={{ width: '60px', height: '24px' }} />
                      <Skeleton.Button active size="small" style={{ width: '70px', height: '24px' }} />
                    </div>
                    <div style={{ width: '100%' }}>
                      <Skeleton active paragraph={false} title={false} style={{ width: '85%', margin: '0 auto', height: '16px' }} />
                    </div>
                  </Card>
                </Col>
              ))
            ) : (
              popularMentors.map((mentor) => (
                <Col xs={12} sm={12} md={6} lg={6} key={mentor.id}>
                <Card 
                  hoverable
                  className="app-card"
                  style={{ textAlign: 'center', height: '100%', cursor: 'pointer' }}
                  bodyStyle={{ padding: '20px 16px' }}
                  onClick={() => {
                      navigate(`/mentor/${mentor.id}`);
                    }}
                >
                  {mentor.profileImage ? (
                  <Avatar 
                    size={80} 
                      src={`data:image/jpeg;base64,${mentor.profileImage}`}
                      style={{ margin: '0 auto 12px', width: 'clamp(60px, 12vw, 80px)', height: 'clamp(60px, 12vw, 80px)' }}
                    />
                  ) : (
                  <Avatar 
                    size={80} 
                    style={{ 
                      margin: '0 auto 12px',
                      background: 'var(--primary-600)',
                      width: 'clamp(60px, 12vw, 80px)',
                      height: 'clamp(60px, 12vw, 80px)'
                    }}
                  >
                      {mentor.firstName?.charAt(0)}{mentor.lastName?.charAt(0)}
                  </Avatar>
                  )}
                  <Title level={4} style={{ 
                    fontSize: 'clamp(0.95rem, 2vw, 1.2rem)',
                    marginBottom: '8px',
                    color: 'var(--text-primary)'
                  }}>
                    {mentor.firstName} {mentor.lastName}
                  </Title>
                  <Text style={{ 
                    color: 'var(--text-secondary)',
                    fontSize: 'clamp(0.8rem, 1.5vw, 0.9rem)',
                    display: 'block',
                    marginBottom: '8px'
                  }}>
                    {mentor.title}
                  </Text>
                  <div style={{ marginBottom: '12px' }}>
                    {mentor.skills?.slice(0, 2).map((skill: string, index: number) => (
                      <Tag key={index} style={{ background: 'var(--primary-100)', color: 'var(--primary-700)', border: 'none' }}>{skill}</Tag>
                    ))}
                  </div>
                  <Text style={{ 
                    color: 'var(--primary-600)',
                    fontSize: '0.9rem',
                    fontWeight: '600'
                  }}>
                    ⭐ {mentor.rating} ({mentor.reviewCount} değerlendirme)
                  </Text>
                </Card>
              </Col>
              ))
            )}
          </Row>

          <div style={{ textAlign: 'center', marginTop: '32px' }}>
            <Button 
              size="large"
              className="btn-primary"
              style={{
                height: '48px',
                padding: '0 24px'
              }}
              onClick={() => {
                navigate('/mentor-search');
              }}
            >
              Tüm Mentörleri Görüntüle
            </Button>
          </div>
        </div>
      </section>
      {/* /Popüler Mentörler Section */}

      {/* Bize Katılın Section */}
      <section style={{ 
        padding: '60px 0', 
        background: 'var(--bg-primary)' 
      }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
          <Row gutter={[24, 24]} align="middle">
            <Col xs={24} lg={12}>
              <div>
                <Title level={2} style={{ 
                  fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
                  fontWeight: '700',
                  marginBottom: '20px',
                  color: 'var(--text-primary)',
                  lineHeight: '1.3'
                }}>
                  Bilgini Paylaşmak veya Yetkinliklerini Artırmak mı İstiyorsun? Bize Katılın
                </Title>
                
                <Paragraph style={{ 
                  fontSize: 'clamp(0.95rem, 2vw, 1.1rem)',
                  marginBottom: '24px',
                  color: 'var(--text-secondary)',
                  lineHeight: '1.6'
                }}>
                  Menteem, bilişim sektöründe bilgi paylaşımını kolaylaştıran ve yetkinliklerini geliştirmek isteyenlere özel bir platform sunar. İster mentör olun ister mentee, kariyerinize değer katacak fırsatlar sizi bekliyor.
                </Paragraph>

                <div style={{ marginBottom: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div style={{ 
                      width: '24px',
                      height: '24px',
                  borderRadius: '50%',
                      background: 'var(--primary-600)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                      marginRight: '12px',
                      marginTop: '2px',
                      flexShrink: 0
                    }}>
                      <span style={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>✓</span>
                </div>
                    <div>
                      <Text style={{ 
                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                        fontWeight: '600',
                        color: 'var(--text-primary)',
                        display: 'block',
                        marginBottom: '4px'
                      }}>
                        Menteeler İçin:
                      </Text>
                      <Text style={{ 
                        fontSize: 'clamp(0.85rem, 1.8vw, 0.95rem)',
                        color: 'var(--text-secondary)',
                        lineHeight: '1.5'
                      }}>
                        Teknik becerilerden iş stratejilerine kadar geniş bir yelpazede eğitim alın. En iyi mentörlerle birebir çalışarak kariyer hedeflerinize ulaşın.
                      </Text>
              </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                <div style={{ 
                      width: '24px',
                      height: '24px',
                  borderRadius: '50%',
                      background: 'var(--primary-600)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                      marginRight: '12px',
                      marginTop: '2px',
                      flexShrink: 0
                    }}>
                      <span style={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>✓</span>
                </div>
                    <div>
                      <Text style={{ 
                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                        fontWeight: '600',
                        color: 'var(--text-primary)',
                        display: 'block',
                        marginBottom: '4px'
                      }}>
                        Mentörler İçin:
                      </Text>
                      <Text style={{ 
                        fontSize: 'clamp(0.85rem, 1.8vw, 0.95rem)',
                        color: 'var(--text-secondary)',
                        lineHeight: '1.5'
                      }}>
                        Bilginizi paylaşarak yeni yeteneklere ilham olun. Menteelerin projelerine rehberlik ederek sektöre katkıda bulunun.
                      </Text>
                    </div>
                  </div>
                </div>

              </div>
            </Col>
            
            <Col xs={24} lg={12}>
              <div style={{ textAlign: 'center' }}>
                <img
                  src="/images/backgrounds/bilgi.png"
                  alt="Bize Katılın"
                  loading="lazy"
                  style={{ 
                    maxWidth: '100%', 
                    height: 'auto',
                    borderRadius: '12px'
                  }}
                />
              </div>
            </Col>
          </Row>
        </div>
      </section>
      {/* /Bize Katılın Section */}

      {/* CTA Section */}
      {/* CTA Section - Sadece giriş yapmamış kullanıcılar için */}
      {!isAuthenticated && (
        <section style={{
          padding: '60px 0', 
          background: 'linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-tertiary) 100%)',
          color: 'var(--text-primary)'
        }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
            <div style={{ textAlign: 'center' }}>
              <Title level={2} style={{ 
                fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
                fontWeight: '600',
                marginBottom: '20px',
                color: 'var(--text-primary)'
              }}>
                Hemen Başlayın
              </Title>
              <Paragraph style={{ 
                fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
                marginBottom: '32px',
                color: 'var(--text-secondary)',
                maxWidth: '600px',
                margin: '0 auto 32px',
                padding: '0 16px'
              }}>
                Kariyerinizde fark yaratmak için bugün Menteem'e katılın
              </Paragraph>
              
              <Space size="middle" wrap style={{ width: '100%', justifyContent: 'center' }}>
                <Button 
                  size="large"
                  style={{
                    background: 'var(--primary-600)',
                    border: 'none',
                    color: 'white',
                    fontWeight: '600',
                    height: '48px',
                    padding: '0 24px',
                    borderRadius: '8px',
                    minWidth: '160px'
                  }}
                >
                  <Link to="/register-mentee" style={{ color: 'white', textDecoration: 'none' }}>
                    Ücretsiz Kayıt Ol
                  </Link>
                </Button>
                <Button 
                  size="large"
                  style={{
                    height: '48px',
                    padding: '0 24px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    border: '2px solid var(--primary-600)',
                    color: 'var(--primary-600)',
                    background: 'transparent',
                    minWidth: '160px'
                  }}
                >
                  <Link to="/login" style={{ color: 'inherit', textDecoration: 'none' }}>
                    Giriş Yap
                  </Link>
                </Button>
              </Space>
            </div>
          </div>
        </section>
      )}
      {/* /CTA Section */}
    </>
  );
};

export default Home;
